import { styled, useDripsyTheme } from 'dripsy';
import React, { Fragment } from 'react';
import { Platform, View } from 'react-native';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { IS_WEB, parseApiError, passIf } from '@utils';
import { Post } from '@models/Posts/PostModel';
import { BaseText } from '../../common/BaseText';
import { AvatarImage } from '../../common/AvatarImage';
import { IconTouchable } from '../../common/IconTouchable';
import { Separator } from '../../common/Separator';
import { ResponsiveIconTouchable } from '../../common/ResponsiveIconTouchable';
import { ToastService } from '@services';
import { TextTruncated } from '../../common/TextTruncated';
import { RichText } from '../../common/RichText';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';
import { User } from '@models/Viewer/UserModel';

const Container = styled(View)({
  pt: '$6',
  pl: '$6',
  pr: '$8',
  pb: '$5',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
});

const AuthorHeader = styled(View)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PostInfoContainer = styled(View)({
  flexDirection: 'row',
});

const AuthorFullName = styled(BaseText)({
  variant: IS_WEB ? 'text.medium' : 'text.small',
  fontWeight: 'semi',
  color: '$heading800',
});

const PostInfo = styled(BaseText)({
  variant: IS_WEB ? 'text.small' : 'text.xsmall',
  color: '$text80',
});

const MessageContainer = styled(View)({
  flex: 1,
});

const MessageText = styled(RichText)({
  color: '$text400',
  variant: 'text.medium',
});

interface ILeftSpacePropsStyle {
  size: number;
}

const LeftSpace = styled(View)((props: ILeftSpacePropsStyle) => {
  // 36 is the size of avatar image in comment view
  return { pl: AVATAR_IMAGE_SIZE + props.size };
});

export interface IPostViewModalParams {
  item: Post;
  imageIndex?: number;
}

const AVATAR_IMAGE_SIZE = 36;

export interface IPostViewTextProps {
  item: Post;
  closeModal(): void;
  onPressUser(user: User): void;
}

export const PostViewText = observer<IPostViewTextProps>(
  ({ item, closeModal, onPressUser }) => {
    const { t } = useTranslation();
    const { theme } = useDripsyTheme();

    async function toggleLike() {
      try {
        await item.itemData.toggleLike.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function onPress() {
      if (item.user && onPressUser) {
        onPressUser(item.user);
      }
    }

    const likeIconName = item.itemData.userStats?.isLiked
      ? 'heart-bold'
      : 'heart-light';

    const likesTitle = t('tutorial.like', {
      count: item.itemData.likes,
    });

    const authorFullName = item.user?.fullName ?? 'Hub';
    const commentsCount = t('common.postView.comment', {
      count: item.comments.totalCount,
    });

    const isDisabledUserNavigation = !item.user;

    return (
      <Container>
        <AuthorHeader>
          <RowContainer>
            {/* If there is no user this means that the post is auto-created
            or created by the admin, in this case, we will show the logo as
            the author's avatar*/}
            {item.user ? (
              <Touchable
                onPress={onPress}
                disabled={isDisabledUserNavigation}
                type={Platform.select({
                  android: TouchableTypeEnum.WithoutFeedback,
                })}
              >
                <AvatarImage
                  source={{ uri: item.user?.photo.small?.url }}
                  size={AVATAR_IMAGE_SIZE}
                />
              </Touchable>
            ) : (
              <AvatarImage
                source={require('../../../../assets/images/logo-icon.png')}
                size={AVATAR_IMAGE_SIZE}
              />
            )}
            <Separator size="$4" vertical />
            <View>
              <Touchable
                onPress={onPress}
                disabled={isDisabledUserNavigation}
                type={Platform.select({
                  android: TouchableTypeEnum.WithoutFeedback,
                })}
              >
                <AuthorFullName numberOfLines={1}>
                  {authorFullName}
                </AuthorFullName>
              </Touchable>
              <PostInfoContainer>
                <PostInfo>
                  {item.itemData.formattedPublishDate}
                </PostInfo>
                <Separator size="$4" vertical />
                <PostInfo>|</PostInfo>
                <Separator size="$4" vertical />
                <PostInfo>{commentsCount}</PostInfo>
              </PostInfoContainer>
            </View>
          </RowContainer>

          <IconTouchable
            onPress={closeModal}
            size={24}
            name="close-light"
          />
        </AuthorHeader>

        <Separator size="$5" />

        <RowContainer>
          <LeftSpace size={theme.space.$4} />
          <MessageContainer>
            {item.itemData.text && (
              <Fragment>
                <TextTruncated numberOfLines={3}>
                  <MessageText>{item.itemData.text}</MessageText>
                </TextTruncated>

                <Separator size="$5" />
              </Fragment>
            )}

            <ResponsiveIconTouchable
              onPress={toggleLike}
              name={likeIconName}
              color={passIf(
                item.itemData.userStats?.isLiked,
                '$error300',
              )}
              title={likesTitle}
              isResponsiveTitle={false}
            />
          </MessageContainer>
        </RowContainer>
      </Container>
    );
  },
);
